import { EditorStarterItem } from '../../items/item-type';

/** 아이템이 가질 수 있는 키프레임 배열 필드 — 마커 이동·삭제가 공통으로 다룬다. */
export const KEYFRAME_FIELDS = [
  'positionKeyframes',
  'zoomKeyframes',
  'pageKeyframes',
] as const;

export type KeyframeField = (typeof KEYFRAME_FIELDS)[number];

type KeyframeEntry = { frame: number };

export type KeyframeArraysSnapshot = Partial<
  Record<KeyframeField, KeyframeEntry[]>
>;

/** 드래그 시작 시점의 키프레임 배열 스냅샷 — 이동 미리보기의 불변 기준. */
export const captureKeyframeArrays = (
  item: EditorStarterItem,
): KeyframeArraysSnapshot => {
  const snapshot: KeyframeArraysSnapshot = {};
  const record = item as EditorStarterItem & KeyframeArraysSnapshot;
  for (const field of KEYFRAME_FIELDS) {
    const arr = record[field];
    if (Array.isArray(arr)) snapshot[field] = arr;
  }
  return snapshot;
};

/**
 * 스냅샷 기준으로 fromFrame(반올림)의 키프레임을 전 타입에서 toFrame 으로 이동한
 * 새 아이템. **매 스텝을 스냅샷에서 다시 계산**하므로 드래그가 다른 키프레임의
 * 프레임을 지나쳐도 그 키프레임은 보존된다 — 증분(prev→next) 이동은 통과 프레임의
 * 키프레임을 교체·삭제해 데이터가 소실된다(리뷰에서 잡힌 사고 패턴).
 * toFrame 에 이미 같은 타입 키프레임이 있으면 교체(upsert 와 동일 의미론) —
 * 이는 드롭 확정 시점의 의도적 덮어쓰기로만 나타난다.
 */
export const applyMovedKeyframes = (
  item: EditorStarterItem,
  snapshot: KeyframeArraysSnapshot,
  fromFrame: number,
  toFrame: number,
): EditorStarterItem => {
  const next = { ...item } as EditorStarterItem & KeyframeArraysSnapshot;
  for (const field of KEYFRAME_FIELDS) {
    const base = snapshot[field];
    if (!Array.isArray(base)) continue;
    if (!base.some((k) => Math.round(k.frame) === fromFrame)) continue;
    next[field] = base
      .filter((k) => {
        const f = Math.round(k.frame);
        return f !== fromFrame && f !== toFrame;
      })
      .concat(
        base
          .filter((k) => Math.round(k.frame) === fromFrame)
          .map((k) => ({ ...k, frame: toFrame })),
      )
      .sort((a, b) => a.frame - b.frame);
  }
  return next;
};

/** 해당(반올림) 프레임의 키프레임을 전 타입에서 제거한 새 아이템(빈 배열은 필드 제거). */
export const removeKeyframesAtFrame = (
  item: EditorStarterItem,
  frame: number,
): EditorStarterItem => {
  const next = { ...item } as EditorStarterItem & KeyframeArraysSnapshot;
  for (const field of KEYFRAME_FIELDS) {
    const arr = next[field];
    if (!Array.isArray(arr)) continue;
    const filtered = arr.filter((k) => Math.round(k.frame) !== frame);
    if (filtered.length === arr.length) continue;
    if (filtered.length === 0) {
      delete next[field];
    } else {
      next[field] = filtered;
    }
  }
  return next;
};
